import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowsClockwise, CircleNotch, ShareNetwork, X } from 'phosphor-react';
import { Button } from '@/components/ui/Button';
import { hap } from '@/lib/haptics';
import { buildShareCaption, toShareCardData } from '@/lib/share/eventCardData';
import { backgroundOptionCount, nextBackgroundIndex } from '@/lib/share/backgrounds';
import { renderEventShareImage, type RenderedShareImage } from '@/lib/share/renderShareImage';
import { eventShareUrl, shareEventImage } from '@/lib/share/shareCard';
import type { EventRow } from '@/types/database';

interface Props {
  event: EventRow | null;
  open: boolean;
  onClose: () => void;
}

// Bottom sheet sur mobile, carte centrée sur desktop.
function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 640px)');
    const update = () => setDesktop(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return desktop;
}

export function ShareEventModal({ event, open, onClose }: Props) {
  const [bgIndex, setBgIndex] = useState<number | null>(null);
  const [image, setImage] = useState<RenderedShareImage | null>(null);
  const [generating, setGenerating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const desktop = useIsDesktop();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Un nouveau fond est tiré à chaque ouverture ; tout est remis à zéro à la fermeture.
  useEffect(() => {
    if (open && event) {
      setNote(null);
      setSharing(false);
      // La photo de l'événement (index 0) est montrée en premier quand elle existe.
      const hasPhoto = Boolean(event.image_url);
      const count = backgroundOptionCount(hasPhoto);
      setBgIndex((prev) => (prev == null ? (hasPhoto ? 0 : nextBackgroundIndex(null, count)) : prev));
    } else {
      setBgIndex(null);
      setImage(null);
      setNote(null);
      setSharing(false);
    }
  }, [open, event]);

  // Génération déterministe à chaque changement de fond.
  useEffect(() => {
    if (!open || !event || bgIndex == null) return;
    let alive = true;
    setGenerating(true);
    (async () => {
      try {
        const rendered = await renderEventShareImage(event, bgIndex);
        if (alive) setImage(rendered);
      } catch {
        if (alive) setNote("Impossible de générer l'image.");
      } finally {
        if (alive) setGenerating(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, event, bgIndex]);

  function regenerate() {
    if (generating || sharing || !event) return;
    hap.light();
    setNote(null);
    // « Autre fond » ne retombe jamais sur le fond affiché.
    const count = backgroundOptionCount(Boolean(event.image_url));
    setBgIndex((prev) => nextBackgroundIndex(prev, count));
  }

  async function share() {
    if (!event || !image || sharing) return;
    hap.light();
    setSharing(true);
    setNote(null);
    const data = toShareCardData(event);
    const caption = buildShareCaption(data, eventShareUrl(event.id));
    const outcome = await shareEventImage({ blob: image.blob, caption, title: data.title });
    setSharing(false);
    if (outcome === 'shared') onClose();
    else if (outcome === 'downloaded') setNote('Image enregistrée — vérifiez vos téléchargements.');
    else if (outcome === 'failed') setNote('Le partage a échoué. Réessayez.');
    // 'cancelled' → annuler n'est pas une erreur : la modale reste ouverte, sans message.
  }

  const ready = Boolean(image) && !generating;

  return (
    <AnimatePresence>
      {open && event ? (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-ink-1/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <div className="fixed inset-0 z-[70] flex items-end justify-center pointer-events-none sm:items-center">
            <motion.div
              className="pointer-events-auto w-full bg-paper-elev ring-1 ring-paper-edge shadow-sheet rounded-t-2xl px-5 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] sm:max-w-[420px] sm:rounded-2xl sm:pb-5"
              initial={desktop ? { opacity: 0, scale: 0.96, y: 10 } : { y: '100%' }}
              animate={desktop ? { opacity: 1, scale: 1, y: 0 } : { y: 0 }}
              exit={desktop ? { opacity: 0, scale: 0.96 } : { y: '100%' }}
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            >
              <div className="mb-2 -mt-1 flex justify-center sm:hidden">
                <span className="block h-1 w-10 rounded-full bg-paper-strong/60" />
              </div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="t-ed-lg">Partager l&apos;événement</h3>
                <button onClick={onClose} aria-label="Fermer" className="grid h-9 w-9 place-items-center rounded-full hover:bg-paper-rec">
                  <X size={18} />
                </button>
              </div>

              <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-paper-rec ring-1 ring-paper-edge">
                {image ? <img src={image.dataUrl} alt="Aperçu de l'image à partager" className="h-full w-full object-cover" /> : null}
                {ready ? null : (
                  <div className="absolute inset-0 grid place-items-center bg-paper-rec/60">
                    <CircleNotch size={30} className="animate-spin text-ink-3" />
                  </div>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <Button variant="secondary" full iconLeft={<ArrowsClockwise size={18} />} onClick={regenerate} disabled={generating || sharing}>
                  Autre fond
                </Button>
                <Button variant="primary" full iconLeft={<ShareNetwork size={18} />} onClick={share} disabled={!ready || sharing} loading={sharing}>
                  Partager
                </Button>
              </div>
              {note ? <p className="t-ui-xs mt-3 text-center text-ink-3">{note}</p> : null}
            </motion.div>
          </div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
